/// <amd-module name="VSS/Diagnostics/Dialog/PerfServerStatsDialog" />
import {
    CheckboxVisibility,
    ColumnActionsMode,
    ConstrainMode,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    IDetailsRowProps
} from "OfficeFabric/DetailsList";
import { Dialog } from "VSSUI/Dialog";
import { Icon } from "VSSUI/Icon";
import { SelectionMode } from "OfficeFabric/Selection";
import { PerfSettingsProps } from "VSS/Diagnostics/PerfBar";
import { IPerfDialogMeasurement, IPerformanceStatistic, IPerformanceView, IVssPerformanceViewService } from "VSS/Diagnostics/PerformanceViewService";
import { Components, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Tooltip } from "VSSUI/TooltipEx";
import React = require("react");

interface PerfServerStatsProps extends IVssComponentProperties {
    performanceView?: IPerformanceView;
    onDismiss: () => any;
}

class PerfServerStatsDialog extends VssComponent<PerfServerStatsProps, {}> {
    private performanceViewService: IVssPerformanceViewService;
    private perfStatisticItems: IPerformanceStatistic[];
    private listColumns: IColumn[];

    constructor(props: PerfSettingsProps) {
        super(props);

        if (props.performanceView) {
            props.performanceView.subscribe(() => {
                this.forceUpdate();
            });
        }

        this.listColumns = [
            this.createColumnHeader("name", "Measurement", true, this.renderStatisticName, 150),
            this.createColumnHeader("start", "Start", false, undefined, 60, 200, "right-aligned-column"),
            this.createColumnHeader("duration", "Duration", true, this.renderDuration, 100, 200, "right-aligned-column"),
            this.createColumnHeader("properties", "Properties", true, this.renderTooltipColumn, 750)
        ];
    }

    public render(): JSX.Element {
        let items: IPerformanceStatistic[] = [];
        let preRequestDuration = 0;
        let requestDuration = 0;
        let fetchResourceDuration = 0;
        let clientSideDuration = 0;
        let pageRequestLabel = "Page Request";
        let showPreRequestColumn = true;

        if (this.props.performanceView) {
            items = this.props.performanceView.performanceItems;

            preRequestDuration = this.props.performanceView.getMeasurementDuration("PreRequest");
            requestDuration = this.props.performanceView.getMeasurementDuration("RequestResponse");
            fetchResourceDuration = this.props.performanceView.getMeasurementDuration("FetchResources");
            clientSideDuration = this.props.performanceView.getMeasurementDuration("Client-side Rendering");

            if (this.props.performanceView.isFastPageSwitchScenario) {
                pageRequestLabel = "FPS Request";
                requestDuration = this.props.performanceView.getMeasurementDuration("FPS Request");
                showPreRequestColumn = false;
            }
        }

        return (
            <Dialog
                calloutClassName="performance-dialog dialog--perf-stats"
                calloutContentClassName="dialog-container dialog-full-size"
                lightDismiss={false}
                onDismiss={this.props.onDismiss}
                showCloseButton={true}
                titleProps={{ text: "Performance Scenarios" }}
            >
                <div className="scenarios-summary">
                    {showPreRequestColumn && <span title="Redirect + DNS + TCP Connect">Pre-Request: {preRequestDuration} ms</span>}
                    <span title="Page Request + Response">
                        {pageRequestLabel}: {requestDuration} ms
                    </span>
                    <span title="Fetch resources (Javascript, CSS), process DOM, execute Javascript modules and render the page (up to TTI)">
                        Client-side processing: {clientSideDuration} ms
                    </span>
                </div>
                <DetailsList
                    ariaLabel={"Performance Stats"}
                    checkboxVisibility={CheckboxVisibility.hidden}
                    className="branches-list"
                    columns={this.listColumns}
                    constrainMode={ConstrainMode.unconstrained}
                    isHeaderVisible={true}
                    items={items}
                    layoutMode={DetailsListLayoutMode.justified}
                    compact={true}
                    selectionMode={SelectionMode.single}
                    onRenderRow={this._renderRow}
                />
            </Dialog>
        );
    }

    private _renderRow(props: IDetailsRowProps, defaultRender: (p: IDetailsRowProps) => JSX.Element): JSX.Element {
        return defaultRender(props);
    }

    private createColumnHeader(
        key: string,
        name: string,
        isResizable: boolean,
        onRender: ((item?: any, index?: number, column?: IColumn) => JSX.Element) | undefined,
        minWidth: number,
        maxWidth?: number,
        className?: string
    ): IColumn {
        return {
            columnActionsMode: ColumnActionsMode.disabled,
            fieldName: key,
            key: key,
            maxWidth: maxWidth,
            minWidth: minWidth,
            name: name,
            isResizable: isResizable,
            className: className,
            onRender: onRender
        };
    }

    private renderStatisticName = (statItem: IPerformanceStatistic, index?: number, column?: IColumn): JSX.Element => {
        return (
            <PerformanceStatisticMeasurementComponent
                depth={statItem.depth}
                statistic={statItem}
                toggle={this.props.performanceView ? this.props.performanceView.toggle : undefined}
            />
        );
    };

    private renderDuration = (statItem: IPerformanceStatistic, index?: number, column?: IColumn): JSX.Element => {
        return <PerformanceStatisticDurationComponent view={this.props.performanceView!} statistic={statItem} />;
    };

    private renderTooltipColumn = (statItem: IPerformanceStatistic, index?: number, column?: IColumn): JSX.Element => {
        return (
            <Tooltip overflowOnly={true} text={statItem.properties}>
                <div className="flex-grow text-ellipsis">{statItem.properties}</div>
            </Tooltip>
        );
    };
}

Components.add("vss.perf-server-stats-dialog", PerfServerStatsDialog);

interface IProps {
    statistic: IPerformanceStatistic;
    view: IPerformanceView;
}

class PerformanceStatisticDurationComponent extends React.Component<IProps> {
    public render(): JSX.Element {
        const { statistic, view } = this.props;

        return (
            <div className="perf-stat-duration">
                <div
                    className="perf-stat-duration-bar"
                    style={{
                        width: `${(statistic.percentage || 0) * 100}%`,
                        opacity: 0.3 + (statistic.percentage || 0) * 1.0
                    }}
                />

                <div className="perf-stat-duration-time">{statistic.duration}</div>
            </div>
        );
    }
}

interface ServerStatisticMeasurementProps {
    depth: number;
    statistic: IPerformanceStatistic;
    toggle: ((measurement: IPerfDialogMeasurement) => boolean) | undefined;
}

class PerformanceStatisticMeasurementComponent extends React.Component<ServerStatisticMeasurementProps, {}> {
    public render(): JSX.Element {
        const spacerElements: JSX.Element[] = [];

        // Generate a spacer for each depth the item sits.
        for (let spacerIndex = 0; spacerIndex < this.props.depth; spacerIndex++) {
            spacerElements.push(<div key={"spacer:" + spacerIndex} />);
        }

        // Determine which Chevron to show based on the expanded state of the statistic.
        let chevronIcon = this.props.statistic.expanded ? "ChevronDownSmall" : "ChevronRightSmall";
        let chevronClass = "statistic-expand-icon icon-centered";
        if (!this.props.statistic.hasChildren) {
            chevronClass = chevronClass + " invisible";
        }

        return (
            <div className="list-cell">
                <div className="list-cell-items-fs" onMouseDown={this.onMouseDown}>
                    <div className="variable-height-text">
                        <div className="spacer-container">{spacerElements}</div>
                        <Icon iconName={chevronIcon} className={chevronClass} />
                        <span>{this.props.statistic.name}</span>
                    </div>
                </div>
            </div>
        );
    }

    private onMouseDown = () => {
        if (this.props.toggle && this.props.statistic.measurement) {
            this.props.toggle(this.props.statistic.measurement);
        }
    };
}
